import { Node, mergeAttributes } from "@tiptap/core";
import { NodeSelection } from "@tiptap/pm/state";
import { ReactNodeViewRenderer } from "@tiptap/react";
import FormulaBox from "../FormulaBox";
import { normalizeFormulaLatex } from "../../../../utils/renderSavedFormulas";

function readLatexFromElement(element) {
  const direct = element.getAttribute("data-latex") || element.getAttribute("data-value");
  if (direct) return normalizeFormulaLatex(direct);

  const annotation = element.querySelector?.('annotation[encoding="application/x-tex"]');
  if (annotation?.textContent) return normalizeFormulaLatex(annotation.textContent);

  const mathfield = element.querySelector?.("math-field");
  if (mathfield) {
    if (typeof mathfield.value === "string") return normalizeFormulaLatex(mathfield.value);
    if (typeof mathfield.getValue === "function") {
      try {
        return normalizeFormulaLatex(mathfield.getValue("latex") || "");
      } catch {}
      try {
        return normalizeFormulaLatex(mathfield.getValue() || "");
      } catch {}
    }
  }

  return normalizeFormulaLatex(element.textContent || "");
}

const FormulaExtension = Node.create({
  name: "formula",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      latex: {
        default: "",
        parseHTML: readLatexFromElement,
        renderHTML: (attributes) => ({
          "data-latex": normalizeFormulaLatex(attributes.latex || ""),
        }),
      },
      displayMode: {
        default: false,
        parseHTML: (element) => {
          const raw = element.getAttribute("data-display-mode") || element.getAttribute("data-formula-block") || "0";
          return raw === "1" || raw === "true";
        },
        renderHTML: (attributes) => ({
          "data-display-mode": attributes.displayMode ? "1" : "0",
        }),
      },
    };
  },

  parseHTML() {
    return [
      { tag: "span[data-formula]" },
      { tag: "span[data-lh6-formula]" },
      { tag: "span.lh6-formula" },
      { tag: "span.lh6-formulaNode" },
      { tag: "div[data-formula-block]" },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const latex = normalizeFormulaLatex(HTMLAttributes["data-latex"] || HTMLAttributes.latex || "");
    const displayMode =
      HTMLAttributes["data-display-mode"] === "1" ||
      HTMLAttributes.displayMode === true ||
      HTMLAttributes.displayMode === "true";

    return [
      "span",
      mergeAttributes(HTMLAttributes, {
        class: `lh6-formulaNode lh6-katexFormula ${displayMode ? "is-block" : "is-inline"}`,
        "data-formula": "1",
        "data-lh6-formula": "1",
        "data-latex": latex,
        "data-display-mode": displayMode ? "1" : "0",
      }),
      latex,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(FormulaBox);
  },

  addCommands() {
    return {
      insertFormula:
        ({ latex = "", displayMode = false } = {}) =>
        ({ state, dispatch }) => {
          const node = this.type.create({ latex, displayMode });
          const { from, to } = state.selection;
          const tr = state.tr.replaceWith(from, to, node);
          tr.setSelection(NodeSelection.create(tr.doc, from));

          if (dispatch) {
            dispatch(tr.scrollIntoView());
          }
          return true;
        },
      updateFormula:
        ({ latex = "", displayMode = false } = {}) =>
        ({ state, dispatch }) => {
          const { selection } = state;
          if (!(selection instanceof NodeSelection)) return false;
          if (selection.node.type !== this.type) return false;

          const tr = state.tr.setNodeMarkup(selection.from, undefined, {
            ...selection.node.attrs,
            latex,
            displayMode,
          });

          if (dispatch) {
            dispatch(tr);
          }
          return true;
        },
      setFormulaDisplayMode:
        (displayMode) =>
        ({ state, dispatch }) => {
          const { selection } = state;
          if (!(selection instanceof NodeSelection)) return false;
          if (selection.node.type !== this.type) return false;

          const tr = state.tr.setNodeMarkup(selection.from, undefined, {
            ...selection.node.attrs,
            displayMode: !!displayMode,
          });

          if (dispatch) {
            dispatch(tr);
          }
          return true;
        },
    };
  },
});

export default FormulaExtension;
